import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ChevronDown } from "lucide-react";
import { useState } from "react";
import { ASSETS } from "#/lib/assets";
import {
  formatList,
  pickupHubHours,
  pickupHubNames,
} from "#/lib/checkout/pickup";
import type { PickupHub } from "#/lib/medusa-types/pickup";
import { pickupQueries } from "#/queries/pickup";
import { PillHero } from "../components/PillHero.js";

export const Route = createFileRoute("/faqs")({
  loader: ({ context: { queryClient } }) =>
    queryClient.ensureQueryData(pickupQueries.hubs()),
  head: () => ({ meta: [{ title: "FAQs" }] }),
  component: FAQsPage,
});

interface FAQ {
  id: string;
  q: string;
  a: string;
}

interface FAQGroup {
  id: string;
  heading: string;
  items: ReadonlyArray<FAQ>;
}

const NO_PICKUP_HUBS = "Pickup isn't available right now.";

function pickupHowAnswer(hubs: PickupHub[]): string {
  if (hubs.length === 0) return NO_PICKUP_HUBS;
  return `Yes. Choose Pickup at checkout, then pick a hub (${formatList(pickupHubNames(hubs), "disjunction")}), a weekday and a time slot. We'll notify you by email and SMS once the order is ready at your chosen hub.`;
}

function pickupWhereAnswer(hubs: PickupHub[]): string {
  if (hubs.length === 0) return NO_PICKUP_HUBS;
  const places = hubs.map((hub) => `${hub.label} (${pickupHubHours(hub)})`);
  return `${formatList(places, "disjunction")}. Full address and hours are sent in the ready-for-pickup email.`;
}

function storeAnswer(hubs: PickupHub[]): string {
  const pickup =
    hubs.length === 0
      ? ""
      : `Our hubs (${formatList(pickupHubNames(hubs), "conjunction")}) support pickup. `;
  return `${pickup}We do not run a walk-in retail counter.`;
}

function faqGroups(hubs: PickupHub[]): ReadonlyArray<FAQGroup> {
  return [
    {
      id: "ordering",
      heading: "Ordering",
      items: [
        {
          id: "ord-1",
          q: "Do I need to be enrolled in Homeschool Global to order?",
          a: "No — our store is open to all homeschool families and educators. Enrollment is not required to purchase any of our titles.",
        },
        {
          id: "ord-2",
          q: "I can't find the book I'm looking for. Do you have it in stock?",
          a: "If a title isn't listed, email support@learningplus.ph with the title and we'll let you know about availability or sourcing options.",
        },
        {
          id: "ord-3",
          q: "When will you process my order?",
          a: "Orders are processed within 1-2 working days. No deliveries or processing happen on Sundays and holidays.",
        },
        {
          id: "ord-4",
          q: "Where else can I buy your books?",
          a: "On our website, plus our Shopee and Lazada storefronts.",
        },
      ],
    },
    {
      id: "shipping",
      heading: "Shipping",
      items: [
        {
          id: "shp-1",
          q: "When can I expect my order to arrive?",
          a: "Metro Manila: 3-5 working days. Provincial: 5-14 working days depending on region. International: 15-20 business days.",
        },
        {
          id: "shp-2",
          q: "I want my books delivered to another country. What are the shipping charges?",
          a: "International shipping is calculated at checkout based on weight and destination. See our International Shipping Policies page for the full breakdown.",
        },
        {
          id: "shp-3",
          q: "Do you ship on weekends?",
          a: "Couriers pick up Monday through Saturday. Orders placed on Sunday begin processing the next working day.",
        },
      ],
    },
    {
      id: "returns",
      heading: "Returns & Refunds",
      items: [
        {
          id: "ret-1",
          q: "How do I return an item?",
          a: "Email orders@learningplus.ph within 7 days of receipt with photos of the item and your order number. See Returns & Refunds for the full policy.",
        },
        {
          id: "ret-2",
          q: "What items can I return?",
          a: "Returns are accepted for damaged, wrong, or significantly different items. Change-of-mind returns are not accepted.",
        },
        {
          id: "ret-3",
          q: "How long until I get my refund?",
          a: "Approved refunds are processed within 7-10 working days back to the original payment method.",
        },
      ],
    },
    {
      id: "payment",
      heading: "Payment",
      items: [
        {
          id: "pay-1",
          q: "What are your payment options?",
          a: "GCash, Maya, BPI / BDO bank transfer, credit/debit cards (Visa, Mastercard), and Cash on Delivery for select areas.",
        },
        {
          id: "pay-2",
          q: "Can I request an Official Receipt in another currency?",
          a: "Official Receipts are issued in PHP, the currency of payment. We cannot reissue ORs in a different currency.",
        },
        {
          id: "pay-3",
          q: "Is it safe to pay by credit card on your site?",
          a: "Yes. Card payments are processed by our PCI-DSS compliant payment partner; we never see or store your card number.",
        },
      ],
    },
    {
      id: "account",
      heading: "Account",
      items: [
        {
          id: "acc-1",
          q: "Do I need an account to order?",
          a: "Guest checkout is available, but creating an account lets you track orders, save addresses, and keep a wishlist.",
        },
        {
          id: "acc-2",
          q: "I forgot my password. How do I reset it?",
          a: "Use the Forgot Password link on the sign-in page. We'll email you a reset link valid for 1 hour.",
        },
        {
          id: "acc-3",
          q: "How do I update my shipping address?",
          a: "Sign in, open My Account, and edit Addresses. Changes apply to new orders only.",
        },
      ],
    },
    {
      id: "pickup",
      heading: "Pickup",
      items: [
        {
          id: "pck-1",
          q: "Can I pick up my order instead of having it delivered?",
          a: pickupHowAnswer(hubs),
        },
        {
          id: "pck-2",
          q: "Where is the pickup location?",
          a: pickupWhereAnswer(hubs),
        },
        {
          id: "pck-3",
          q: "How long do I have to claim a pickup order?",
          a: "Pickup orders are held for 14 days. Unclaimed orders after that are restocked and refunded to store credit.",
        },
      ],
    },
    {
      id: "other",
      heading: "Other",
      items: [
        {
          id: "oth-1",
          q: "Do you offer discounts for bulk or school orders?",
          a: "Yes. Email support@learningplus.ph with your school name, list of titles, and quantities for a custom quote.",
        },
        {
          id: "oth-2",
          q: "Can I request a title you don't currently carry?",
          a: "We take requests. Send the title, author, and ISBN if you have it; we'll check sourcing options.",
        },
        {
          id: "oth-3",
          q: "Do you have a physical bookstore?",
          a: storeAnswer(hubs),
        },
      ],
    },
  ];
}

function FAQsPage() {
  const [openId, setOpenId] = useState<string | null>(null);
  const { data: hubs = [] } = useQuery(pickupQueries.hubs());
  return (
    <div data-screen-label="FAQs" className="pb-20">
      <PillHero
        content={
          <h1 className="m-0 font-bold font-display text-4xl text-lp-green-dk leading-[1.05] md:text-6xl">
            FREQUENTLY
            <br />
            ASKED
            <br />
            QUESTIONS
          </h1>
        }
        asset={
          <img
            src={ASSETS.heroFaq}
            alt=""
            className="h-56 w-auto object-cover md:h-96 md:-mb-20 md:-ml-12"
          />
        }
        assetAlign="edge"
        assetClassName="md:-my-28 md:-ml-24"
      />

      <div className="mx-auto w-full max-w-3xl px-6 md:px-10">
        {faqGroups(hubs).map((group) => (
          <section key={group.id} className="mb-10 md:mb-12">
            <h2 className="m-0 mb-2 font-display font-semibold text-xl text-lp-green-dk md:mb-3 md:text-2xl">
              {group.heading}
            </h2>
            <ul className="m-0 list-none p-0">
              {group.items.map((f) => {
                const isOpen = openId === f.id;
                return (
                  <li
                    key={f.id}
                    className="border-lp-green-mid/40 border-b last:border-b-0"
                  >
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      onClick={() => setOpenId(isOpen ? null : f.id)}
                      className="flex w-full cursor-pointer items-center justify-between gap-4 border-0 bg-transparent py-4 text-left md:py-5"
                    >
                      <span className="font-display text-base text-lp-text-dk">
                        {f.q}
                      </span>
                      <ChevronDown
                        aria-hidden="true"
                        size={20}
                        className={`shrink-0 text-lp-green-mid transition-transform ${isOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                    {isOpen && (
                      <p className="m-0 max-w-2xl pb-4 font-display text-base text-lp-text leading-relaxed md:pb-5">
                        {f.a}
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}
